import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getKidSub } from '@/auth/authStore';
import { ApiError } from '@/lib/api';
import {
  getTranscript,
  quoteTranscript,
  extractTranscript,
  confirmTranscript,
  type TranscriptQuote,
  type TranscriptExtract,
  type TranscriptConfirm,
  type StudyTranscript,
} from './studyTranscriptsApi';
import { StudyMapCoach } from './StudyMapCoach';

export function StudyTranscriptPanel({
  documentId,
  hasSubject,
  onUnsavedChange,
}: {
  documentId: string;
  hasSubject: boolean;
  onUnsavedChange: (dirty: boolean) => void;
}) {
  const owner = getKidSub();
  const live = useRef(true);
  const client = useQueryClient();
  const key = ['study-transcript', owner, documentId];
  const query = useQuery({
    queryKey: key,
    queryFn: () => getTranscript(documentId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [saved, setSaved] = useState<StudyTranscript | null>(null);
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [quote, setQuote] = useState<TranscriptQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [extract, setExtract] = useState<TranscriptExtract | null>(null);
  const [confirm, setConfirm] = useState<TranscriptConfirm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const acceptedVersion = useRef(0);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const alive = () => live.current && getKidSub() === owner;
  useEffect(() => {
    if (query.isSuccess && query.data && query.data.version > acceptedVersion.current) {
      acceptedVersion.current = query.data.version;
      setSaved(query.data);
      setText(query.data.text);
      setSelected([]);
    }
  }, [query.isSuccess, query.data]);
  const dirty = Boolean(
    extract || confirm || busy || (saved && text !== saved.text) || selected.length,
  );
  useEffect(() => onUnsavedChange(dirty), [dirty, onUnsavedChange]);
  function failure(reason: unknown) {
    setError(
      reason instanceof Error
        ? reason.message
        : 'This change could not be saved. Your draft stays here.',
    );
    setConflict(reason instanceof ApiError && reason.code === 'STUDY_TRANSCRIPT_VERSION_CHANGED');
  }
  function accept(result: StudyTranscript) {
    acceptedVersion.current = result.version;
    setSaved(result);
    setText(result.text);
    setSelected([]);
    setExtract(null);
    setConfirm(null);
    setQuote(null);
    setConflict(false);
    setError(null);
    client.setQueryData(key, result);
  }
  async function getQuote() {
    setBusy(true);
    setError(null);
    try {
      const result = await quoteTranscript(documentId);
      if (alive()) setQuote(result);
    } catch (reason) {
      if (alive()) failure(reason);
    } finally {
      if (alive()) setBusy(false);
    }
  }
  async function recognize(input: TranscriptExtract) {
    setExtract(input);
    setBusy(true);
    setError(null);
    try {
      const result = await extractTranscript(documentId, input);
      if (alive()) {
        accept(result);
        setNotice('Recognition saved as unverified. Check the image and correct the text.');
      }
    } catch (reason) {
      if (alive()) failure(reason);
    } finally {
      if (alive()) setBusy(false);
    }
  }
  async function saveConfirmation(input: TranscriptConfirm) {
    setConfirm(input);
    setBusy(true);
    setError(null);
    try {
      const result = await confirmTranscript(documentId, input);
      if (alive()) {
        accept(result);
        setNotice(
          'Your confirmed text is searchable. Selected concepts are saved with their source.',
        );
      }
    } catch (reason) {
      if (alive()) failure(reason);
    } finally {
      if (alive()) setBusy(false);
    }
  }
  async function reload() {
    setBusy(true);
    setError(null);
    try {
      const result = await getTranscript(documentId);
      if (alive()) {
        if (result) accept(result);
        else {
          acceptedVersion.current = 0;
          setSaved(null);
          setText('');
          setExtract(null);
          setConfirm(null);
          setSelected([]);
          setConflict(false);
        }
      }
    } catch (reason) {
      if (alive()) failure(reason);
    } finally {
      if (alive()) setBusy(false);
    }
  }
  return (
    <section
      className="border-t border-hairline p-5 text-sm text-ink"
      aria-label="Photo text recognition"
    >
      <h3 className="text-lg font-extrabold">Recognize this page</h3>
      <p className="mt-2 text-slate2">
        Check the original beside the recognition draft. Only text you confirm enters recognition
        search.
      </p>
      {query.isError ? (
        <p role="alert" className="mt-3">
          Saved recognition could not be loaded.{' '}
          <button className="min-h-11 font-bold underline" onClick={() => void query.refetch()}>
            Retry recognition load
          </button>
        </p>
      ) : (
        <button
          type="button"
          className="btn-pill-secondary mt-3"
          disabled={!query.isSuccess || dirty || Boolean(quote)}
          onClick={() => void getQuote()}
        >
          Review recognition price
        </button>
      )}
      {quote && (
        <div className="mt-3 rounded-2xl bg-wash-sky p-4">
          <p>{quote.scope}</p>
          <p className="mt-2 font-bold">{quote.stars} Stars · one selected page</p>
          <button
            type="button"
            className="btn-pill-primary mt-3"
            disabled={busy || Boolean(extract)}
            onClick={() =>
              void recognize({
                request_id: crypto.randomUUID(),
                expected_version: saved?.version ?? 0,
                expected_stars: quote.stars,
                page_number: quote.page_number,
                source_revision: quote.source_revision,
              })
            }
          >
            Recognize page · {quote.stars} Stars
          </button>
          <button
            type="button"
            className="btn-pill-secondary ml-2 mt-3"
            disabled={busy}
            onClick={() => setQuote(null)}
          >
            Cancel recognition
          </button>
        </div>
      )}
      {saved && (
        <div className="mt-4">
          <p className="font-bold">
            {saved.state === 'confirmed'
              ? 'Learner-confirmed transcription'
              : 'AI recognition · unverified'}{' '}
            · version {saved.version}
          </p>
          {saved.warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {saved.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          <label className="mt-3 block font-bold" htmlFor="study-recognition-text">
            Check and correct recognized text
          </label>
          <textarea
            id="study-recognition-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={12000}
            disabled={busy || Boolean(extract || confirm)}
            rows={9}
            className="mt-2 w-full rounded-xl border border-hairline bg-canvas p-3"
          />
          {saved.concepts.length > 0 && (
            <fieldset
              disabled={!hasSubject || busy || Boolean(extract || confirm)}
              className="mt-3"
            >
              <legend className="font-bold">Choose concepts you checked against this page</legend>
              {saved.state === 'confirmed' && (
                <p className="mt-2 text-slate2">
                  When correcting the text, check these concepts again. Earlier concepts and maps
                  are marked outdated when you save the correction.
                </p>
              )}
              {saved.concepts.map((c, i) => (
                <label key={i} className="mt-2 block rounded-xl border border-hairline p-3">
                  <input
                    type="checkbox"
                    checked={selected.includes(i)}
                    onChange={(e) =>
                      setSelected((s) => (e.target.checked ? [...s, i] : s.filter((n) => n !== i)))
                    }
                    className="mr-2"
                  />
                  <strong>{c.title}</strong>
                  <span className="mt-1 block text-slate2">{c.body}</span>
                </label>
              ))}
            </fieldset>
          )}
          {!hasSubject && saved.concepts.length > 0 && (
            <p className="mt-2">
              Choose a subject in Materials before saving concepts. You can confirm the text now.
            </p>
          )}
          <button
            type="button"
            disabled={
              busy ||
              !text.trim() ||
              Boolean(extract || confirm) ||
              (saved.state === 'confirmed' && text === saved.text)
            }
            className="btn-pill-primary mt-3"
            onClick={() =>
              void saveConfirmation({
                request_id: crypto.randomUUID(),
                expected_version: saved.version,
                source_revision: saved.source_revision,
                page_number: saved.page_number,
                text: text.trim(),
                concepts: selected.map((i) => saved.concepts[i]),
              })
            }
          >
            Confirm corrected text
          </button>
          {saved.state === 'confirmed' && hasSubject && saved.concept_ids.length >= 2 && (
            <StudyMapCoach
              documentId={documentId}
              label="Build a map from my confirmed concepts"
              enabled={!dirty}
              onSaved={async () => {
                setNotice('Mind map saved with its original source.');
              }}
            />
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-xl bg-wash-coral p-3">
          {error}
        </p>
      )}
      {(extract || confirm) && !conflict && (
        <button
          type="button"
          disabled={busy}
          className="btn-pill-secondary mt-3"
          onClick={() =>
            void (extract ? recognize(extract) : confirm ? saveConfirmation(confirm) : undefined)
          }
        >
          Retry same recognition request
        </button>
      )}
      {(extract || confirm || conflict) && (
        <button
          type="button"
          disabled={busy}
          className="btn-pill-secondary mt-3"
          onClick={() => void reload()}
        >
          Discard draft and reload saved recognition
        </button>
      )}
      <p role="status" className="mt-3">
        {busy ? 'Working… Keep this page open.' : notice}
      </p>
    </section>
  );
}
